import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../services/NotificationService';

/**
 * Bridges notification deep-links into React Router. Rendered inside <Router>
 * so it can call useNavigate(); it registers that navigate fn with the
 * NotificationService so a notification click routes through the SPA instead
 * of setting window.location.hash (which BrowserRouter ignores).
 */
const NotificationNavigationBridge: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    notificationService.setNavigationHandler((path: string) => navigate(path));
    return () => notificationService.setNavigationHandler(null);
  }, [navigate]);

  // A push clicked while the portal is already open: the service worker
  // focuses this tab and posts the target here, since it cannot drive the
  // router itself. Without this the tab came to the front and stayed put.
  useEffect(() => {
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
    if (!sw) return undefined;
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'NOTIFICATION_CLICK') return;
      const target = event.data.targetUrl;
      // A path in this app only - never `//host` or `/\host`.
      if (typeof target === 'string' && /^\/(?![\\/])/.test(target)) navigate(target);
    };
    sw.addEventListener('message', onMessage);
    return () => sw.removeEventListener('message', onMessage);
  }, [navigate]);

  return null;
};

export default NotificationNavigationBridge;
